import React from 'react';
import { FormattedMessage } from '../../../util/reactIntl';
import { ListingCard, ListingCardSkeletons, NamedLink } from '../../../components';

import { IconArrow } from './Icons';
import ScrollRow from './ScrollRow';
import css from './SectionFeaturedProducts.module.css';

const SKELETON_COUNT = 6;
// Enough to cover the visible part of the row on the widest layout.
const EAGER_IMAGE_COUNT = 6;

/**
 * Featured products row.
 *
 * The section is always exactly one row tall, however many listings Sharetribe
 * returns: the overflow is reached by scrolling sideways (arrow buttons or a
 * swipe) rather than by the section growing into a multi-row grid. Nothing is
 * hardcoded — everything comes from whatever Sharetribe returns.
 */
const SectionFeaturedProducts = props => {
  const { listings = [], queryInProgress, queryError } = props;
  const hasListings = listings.length > 0;

  return (
    <section id="featured-products" className={css.root}>
      <div className={css.inner}>
        <header className={css.header}>
          <div>
            <h2 className={css.title}>
              <FormattedMessage
                id="LandingPage.featured.title"
                defaultMessage="Featured products"
              />
            </h2>
            <p className={css.subtitle}>
              <FormattedMessage
                id="LandingPage.featured.subtitle"
                defaultMessage="A curated selection of products with verified sustainability credentials."
              />
            </p>
          </div>
        </header>

        {queryInProgress ? (
          <ScrollRow>{ListingCardSkeletons({ count: SKELETON_COUNT })}</ScrollRow>
        ) : hasListings ? (
          <ScrollRow>
            {listings.map((l, i) => (
              <ListingCard
                key={l.id.uuid}
                className={css.card}
                listing={l}
                // This row is at the top of the page, so its first photos load
                // straight away instead of waiting for the lazy-loader.
                lazyLoadImage={i >= EAGER_IMAGE_COUNT}
              />
            ))}
          </ScrollRow>
        ) : (
          <div className={css.empty}>
            <p className={css.emptyText}>
              {queryError ? (
                <FormattedMessage
                  id="LandingPage.featured.error"
                  defaultMessage="We couldn't load products right now. Please try again shortly."
                />
              ) : (
                <FormattedMessage
                  id="LandingPage.featured.empty"
                  defaultMessage="New products with verified credentials are on their way. Check back soon."
                />
              )}
            </p>
            <NamedLink name="SearchPage" className={css.emptyCta}>
              <FormattedMessage
                id="LandingPage.featured.emptyCta"
                defaultMessage="Browse the marketplace"
              />
              <IconArrow className={css.viewAllArrow} />
            </NamedLink>
          </div>
        )}

        {hasListings || queryInProgress ? (
          <div className={css.footer}>
            <NamedLink name="SearchPage" className={css.viewAll}>
              <FormattedMessage id="LandingPage.featured.viewAll" defaultMessage="View all" />
              <IconArrow className={css.viewAllArrow} />
            </NamedLink>
          </div>
        ) : null}
      </div>
    </section>
  );
};

export default SectionFeaturedProducts;
